<template>
  <div class="glass2" v-if="showSalesDashboard">
    <!-- Dashboard Header -->
    <div class="dashboard-header">
      <div>
        <h6 style="text-align: left; font-weight:bold; margin:0;">📊 Sales Report</h6>
        <small class="text-muted-foreground">Real-time analytics & performance metrics</small>
      </div>
      <div class="header-right">
        <b-badge variant="light" class="date-badge">
          <b-icon-calendar class="mr-1" size="sm" />
          {{ formattedDateRange }}
        </b-badge>
      </div>
    </div>

    <!-- Period Tabs - Stats Cards -->
    <div class="period-tabs-wrapper">
      <div class="period-tabs">
        <div 
          v-for="tab in periodTabs" 
          :key="tab.key"
          class="period-tab"
          :class="{ 'active': activePeriod === tab.key }"
          @click="selectPeriod(tab.key)"
        >
          <div class="tab-label">{{ tab.label }}</div>
          <div class="tab-value">${{ formatNumber(tab.value) }}</div>
          <div v-if="tab.change !== null && !isNaN(tab.change)" class="tab-change" :class="tab.change >= 0 ? 'positive' : 'negative'">
            <i :class="tab.change >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
            {{ Math.abs(tab.change).toFixed(1) }}%
          </div>
          <div class="tab-sub">{{ tab.count }} transactions</div>
        </div>
      </div>
    </div>

    <!-- Active Period Banner -->
    <div v-if="activePeriod" class="active-period-banner">
      <div class="banner-content">
        <span class="banner-label">{{ activePeriodLabel }}</span>
        <span class="banner-value">${{ formatNumber(activePeriodValue) }}</span>
        <span class="banner-sub">{{ activePeriodCount }} transactions</span>
      </div>
      <b-button variant="light" size="sm" @click="clearPeriodFilter" class="banner-clear">
        <i class="fas fa-times"></i>
      </b-button>
    </div>

    <!-- Filters - Below Period Tabs, Shown when a period is selected -->
    <div v-if="activePeriod" class="filters-bar">
      <b-card class="filters-card" no-body>
        <b-card-body class="p-3">
          <div class="row align-items-end">
            <div class="col-lg-8">
              <div class="d-flex flex-wrap align-items-end" style="gap: 10px;">
                <div style="min-width: 200px;">
                  <label class="small text-muted mb-1">
                    <i class="fas fa-calendar mr-1"></i>DATE RANGE
                  </label>
                  <date-picker
                    v-model="dateRange"
                    format="MM-DD-YYYY"
                    value-type="format"
                    class="custom-datepicker"
                    placeholder="SELECT DATE RANGE"
                    range
                  ></date-picker>
                </div>

                <div v-if="locationOptions.length" style="min-width: 150px;">
                  <label class="small text-muted mb-1">LOCATION</label>
                  <b-form-select
                    v-model="filters.location"
                    :options="locationOptions"
                    size="sm"
                    @change="onFilterChange"
                    class="filter-select"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Locations</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div v-if="salespersonOptions.length" style="min-width: 150px;">
                  <label class="small text-muted mb-1">SALESPERSON</label>
                  <b-form-select
                    v-model="filters.salesperson"
                    :options="salespersonOptions"
                    size="sm"
                    @change="onFilterChange"
                    class="filter-select"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Salespeople</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div style="min-width: 150px;">
                  <label class="small text-muted mb-1">PAYMENT METHOD</label>
                  <b-form-select
                    v-model="filters.paymentMethod"
                    :options="paymentMethodOptions"
                    size="sm"
                    @change="onFilterChange"
                    class="filter-select"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Methods</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div style="min-width: 130px;">
                  <label class="small text-muted mb-1">SALE TYPE</label>
                  <b-form-select
                    v-model="filters.saleType"
                    :options="saleTypeOptions"
                    size="sm"
                    @change="onFilterChange"
                    class="filter-select"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Types</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div>
                  <b-button 
                    size="sm" 
                    variant="primary" 
                    @click="fetchMainData"
                    :disabled="isLoading"
                    class="analyze-btn"
                  >
                    <i class="fas fa-search" v-if="!isLoading"></i>
                    <i class="fas fa-spinner fa-spin" v-else></i>
                    {{ isLoading ? '' : 'ANALYZE' }}
                  </b-button>
                </div>
              </div>
            </div>
            
            <div class="col-lg-4 mt-2 mt-lg-0">
              <div class="d-flex justify-content-end">
                <div v-if="activeFilterCount > 0">
                  <b-button variant="link" size="sm" @click="clearAllFilters" class="p-0 clear-filters">
                    <i class="fas fa-times-circle mr-1"></i>Clear {{ activeFilterCount }} filter(s)
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading sales data...</p>
    </div>

    <!-- Main Content - Only shown when data is loaded and a period is selected -->
    <div v-if="!isLoading && hasData && activePeriod" class="content-wrapper">
      <!-- Summary Stats -->
      <div class="modules-container summary-stats">
        <div class="module-card stat-card">
          <div class="stat-icon" style="color: #4A90E2;">
            <i class="fas fa-chart-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">${{ formatNumber(filteredSummaryData.total_sales) }}</div>
            <div class="stat-label">Total Sales</div>
          </div>
        </div>

        <div class="module-card stat-card">
          <div class="stat-icon" style="color: #50E3C2;">
            <i class="fas fa-shopping-cart"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">${{ formatNumber(filteredSummaryData.total_net_sales) }}</div>
            <div class="stat-label">Net Sales</div>
          </div>
        </div>

        <div class="module-card stat-card">
          <div class="stat-icon" style="color: #F5A623;">
            <i class="fas fa-shopping-bag"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ filteredSummaryData.total_orders }}</div>
            <div class="stat-label">Orders</div>
          </div>
        </div>

        <div class="module-card stat-card">
          <div class="stat-icon" style="color: #D0029B;">
            <i class="fas fa-calculator"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">${{ formatNumber(filteredSummaryData.total_net_sales / (filteredSummaryData.total_orders || 1)) }}</div>
            <div class="stat-label">Avg Order</div>
          </div>
        </div>
      </div>

      <!-- Payment Methods -->
      <b-row>
        <b-col lg="12" class="mb-3">
          <b-card class="table-card" no-body>
            <b-card-header class="table-header">
              <i class="fas fa-credit-card mr-2"></i>
              <span>Payment Methods</span>
              <b-badge variant="light" class="ml-2">{{ paymentMethodStats.length }}</b-badge>
            </b-card-header>
            <b-card-body class="p-3">
              <b-row>
                <b-col lg="5" class="mb-3 mb-lg-0">
                  <div class="pie-chart-container">
                    <canvas ref="pieChart" id="paymentPieChart"></canvas>
                  </div>
                </b-col>
                <b-col lg="7">
                  <div class="payment-methods">
                    <div 
                      v-for="(item, index) in paymentMethodStats" 
                      :key="index"
                      class="payment-item"
                      @click="drillDownPaymentMethod(item)"
                      :class="{ 'drill-active': isPaymentMethodActive(item.method) }"
                    >
                      <div class="payment-row">
                        <span class="payment-name">
                          <span class="payment-dot" :style="{ background: colors[index % colors.length] }"></span>
                          {{ item.method }}
                          <b-badge v-if="item.subMethods && item.subMethods.length > 0" variant="light" size="sm" class="ml-1">
                            {{ item.subMethods.length }}
                          </b-badge>
                        </span>
                        <span class="payment-amount">${{ formatNumber(item.amount) }}</span>
                      </div>
                      <div class="payment-bar">
                        <div 
                          class="payment-bar-fill" 
                          :style="{ width: item.percentage + '%', background: colors[index % colors.length] }"
                        />
                      </div>
                      <div class="payment-row small text-muted">
                        <span>{{ item.count }} transactions</span>
                        <span>{{ item.percentage }}%</span>
                      </div>
                      
                      <!-- Sub-methods for Finance or Tax Exempt -->
                      <div v-if="item.subMethods && item.subMethods.length > 0" class="sub-methods mt-1">
                        <div 
                          v-for="(sub, subIndex) in item.subMethods" 
                          :key="subIndex"
                          class="sub-method-item"
                          @click.stop="drillDown('paymentMethod', sub.name)"
                          :class="{ 'drill-active': filters.paymentMethod === sub.name }"
                        >
                          <span class="sub-method-name">{{ sub.name }}</span>
                          <span class="sub-method-amount">${{ formatNumber(sub.amount) }}</span>
                        </div>
                      </div>
                    </div>
                    <div v-if="!paymentMethodStats.length" class="text-center py-3 text-muted">
                      No payment methods
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Location & Salesperson Performance -->
      <div v-if="hasActiveFilters">
        <b-row>
          <b-col lg="6" class="mb-3">
            <b-card class="table-card" no-body>
              <b-card-header class="table-header d-flex justify-content-between align-items-center">
                <div>
                  <i class="fas fa-building mr-2"></i>
                  <span>Performance by Location</span>
                  <b-badge variant="light" class="ml-2">{{ filteredLocationStats.length }}</b-badge>
                </div>
              </b-card-header>
              <b-card-body class="p-0">
                <div class="table-wrap">
                  <table class="performance-table">
                    <thead>
                      <tr>
                        <th>Location</th>
                        <th class="text-right">Sales</th>
                        <th class="text-right">Net</th>
                        <th class="text-right">Orders</th>
                        <th class="text-right">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr 
                        v-for="item in filteredLocationStats" 
                        :key="item.location_id"
                        @click="drillDown('location', item.location_id)"
                        :class="{ 'drill-active': filters.location === item.location_id }"
                      >
                        <td>{{ item.location_name }}</td>
                        <td class="text-right">${{ formatNumber(item.sales) }}</td>
                        <td class="text-right">${{ formatNumber(item.net) }}</td>
                        <td class="text-right">{{ item.orders }}</td>
                        <td class="text-right">{{ item.percentage }}%</td>
                      </tr>
                    </tbody>
                    <tfoot v-if="filteredLocationStats.length">
                      <tr>
                        <td><strong>Total</strong></td>
                        <td class="text-right"><strong>${{ formatNumber(filteredTotalLocationSales) }}</strong></td>
                        <td class="text-right"><strong>${{ formatNumber(filteredTotalLocationNet) }}</strong></td>
                        <td class="text-right"><strong>{{ filteredTotalLocationOrders }}</strong></td>
                        <td class="text-right"><strong>100%</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <b-col lg="6" class="mb-3">
            <b-card class="table-card" no-body>
              <b-card-header class="table-header">
                <i class="fas fa-users mr-2"></i>
                <span>Performance by Salesperson</span>
                <b-badge variant="light" class="ml-2">{{ filteredSalespersonStats.length }}</b-badge>
              </b-card-header>
              <b-card-body class="p-0">
                <div class="table-wrap">
                  <table class="performance-table">
                    <thead>
                      <tr>
                        <th>Salesperson</th>
                        <th class="text-right">Sales</th>
                        <th class="text-right">Net</th>
                        <th class="text-right">Orders</th>
                        <th class="text-right">Avg Order</th>
                        <th class="text-right">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr 
                        v-for="item in filteredSalespersonStats" 
                        :key="item.salesperson"
                        @click="drillDown('salesperson', item.salesperson)"
                        :class="{ 'drill-active': filters.salesperson === item.salesperson }"
                      >
                        <td>{{ item.salesperson }}</td>
                        <td class="text-right">${{ formatNumber(item.sales) }}</td>
                        <td class="text-right">${{ formatNumber(item.net) }}</td>
                        <td class="text-right">{{ item.orders }}</td>
                        <td class="text-right">${{ formatNumber(item.avg_order) }}</td>
                        <td class="text-right">{{ item.percentage }}%</td>
                      </tr>
                    </tbody>
                    <tfoot v-if="filteredSalespersonStats.length">
                      <tr>
                        <td><strong>Total</strong></td>
                        <td class="text-right"><strong>${{ formatNumber(filteredTotalSalespersonSales) }}</strong></td>
                        <td class="text-right"><strong>${{ formatNumber(filteredTotalSalespersonNet) }}</strong></td>
                        <td class="text-right"><strong>{{ filteredTotalSalespersonOrders }}</strong></td>
                        <td class="text-right"><strong>${{ formatNumber(filteredTotalSalespersonAvgOrder) }}</strong></td>
                        <td class="text-right"><strong>100%</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <!-- Message when no filters applied -->
      <div v-else class="text-center py-4 bg-light rounded">
        <i class="fas fa-filter-circle text-muted" style="font-size: 32px;"></i>
        <p class="mt-2 text-muted">Select a Location, Salesperson, or Payment Method to view detailed performance</p>
      </div>

      <!-- Detailed Transactions -->
      <div v-if="showDetailed && hasActiveFilters" class="detailed-section">
        <b-card class="table-card" no-body>
          <b-card-header class="table-header d-flex justify-content-between align-items-center">
            <div>
              <i class="fas fa-table mr-2"></i>
              <span>Detailed Transactions</span>
              <b-badge variant="secondary" class="ml-2">{{ filteredTransactions.length }}</b-badge>
            </div>
            <div>
              <b-button size="sm" variant="outline-secondary" @click="exportCSV" class="mr-1">
                <i class="fas fa-download mr-1"></i>Export
              </b-button>
              <b-button size="sm" variant="outline-danger" @click="showDetailed = false">
                <i class="fas fa-times"></i>Close
              </b-button>
            </div>
          </b-card-header>
          <b-card-body class="p-0">
            <div class="table-wrap" style="max-height: 400px;">
              <table class="performance-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice</th>
                    <th>Location</th>
                    <th>Salesperson</th>
                    <th>Payment</th>
                    <th class="text-right">Amount</th>
                    <th class="text-center">Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in paginatedTransactions" :key="item.invoice_id + item.ts">
                    <td>{{ formatDate(item.ts) }}</td>
                    <td>{{ item.invoice_id }}</td>
                    <td>{{ getLocationName(item.location_id) }}</td>
                    <td>{{ item.username }}</td>
                    <td>{{ item.payment_methods ? item.payment_methods.join(', ') : '-' }}</td>
                    <td class="text-right" :class="{ 'text-danger': item.total_dollar_amount < 0 }">
                      ${{ formatNumber(item.total_dollar_amount) }}
                    </td>
                    <td class="text-center">
                      <b-badge :variant="item.is_refund_entry ? 'danger' : 'success'" pill>
                        {{ item.is_refund_entry ? 'Refund' : 'Sale' }}
                      </b-badge>
                    </td>
                  </tr>
                  <tr v-if="!filteredTransactions.length">
                    <td colspan="7" class="text-center py-3 text-muted">No transactions found</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="filteredTransactions.length" class="p-2 border-top d-flex justify-content-between align-items-center">
              <small class="text-muted">
                {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, filteredTransactions.length) }} of {{ filteredTransactions.length }}
              </small>
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredTransactions.length"
                :per-page="pageSize"
                size="sm"
                class="mb-0"
              />
              <b-form-select v-model="pageSize" size="sm" style="width: 70px;">
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
              </b-form-select>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </div>

    <!-- No Data - Show when no period is selected -->
    <div v-if="!isLoading && !hasData && !activePeriod" class="empty-dashboard">
      <div class="empty-icon">
        <i class="fas fa-chart-bar"></i>
      </div>
      <h5>Select a Period</h5>
      <p>Click on a period tab above to view sales data</p>
    </div>
    
    <!-- No Data - Show when period selected but no data -->
    <div v-if="!isLoading && !hasData && activePeriod" class="empty-dashboard">
      <div class="empty-icon">
        <i class="fas fa-chart-bar"></i>
      </div>
      <h5>No Data Available</h5>
      <p>No transactions found for the selected period</p>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import Chart from 'chart.js';
import axios from 'axios';
import Requestor from '@/utilities/Requestor';

export default {
  name: 'SalesReport',
  components: { DatePicker },
  props: {
    userDetails: { type: Object, default: () => ({}) },
    showSalesDashboard: { type: Boolean, default: false }
  },
  
  data() {
    return {
      isLoading: false,
      showDetailed: false,
      activePeriod: null,
      expandedPaymentMethod: null,
      
      dateRange: [],
      filters: {
        location: null,
        salesperson: null,
        paymentMethod: null,
        saleType: null
      },
      
      kpiData: {
        today: 0,
        yesterday: 0,
        this_week: 0,
        last_week: 0,
        this_month: 0,
        last_month: 0,
        today_count: 0,
        yesterday_count: 0,
        this_week_count: 0,
        last_week_count: 0,
        this_month_count: 0,
        last_month_count: 0,
        this_week_change: null,
        this_month_change: null,
        yesterday_change: null
      },
      
      // Raw data from API
      rawSummaryData: null,
      rawLocationSummary: {},
      rawSalespersonSummary: {},
      transactions: [],
      filteredTransactions: [],
      
      currentPage: 1,
      pageSize: 25,
      
      pieChart: null,
      
      packet: { userDetails: this.userDetails || {}, locations: [] },
      
      colors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#f472b6', '#34d399'],
      
      periodMap: {
        today: 'Today',
        yesterday: 'Yesterday',
        this_week: 'This Week',
        last_week: 'Last Week',
        this_month: 'This Month',
        last_month: 'Last Month'
      },
      
      financeMethods: ['ACIMA CREDIT', 'AMERICAN FIRST', 'WELLS FARGO', 'SNAP FINANCE', 'PROGRESSIVE', 'CHECK PERSONAL'],
      taxExemptMethods: ['TAX EXEMPT'],
      
      locationMap: {
        16: "HUNTING PARK", 44: "ONLINE SALES", 29: "LANCASTER",
        25: "ALLENTOWN", 27: "READING", 28: "EWING", 22: "LANSDOWNE",
        17: "UPPER DARBY", 46: "OUT OF STOCK", 40: "9TH ST",
        36: "VENANGO", 24: "DELMAR", 47: "MECCA SHIPPING",
        42: "101 WH", 41: "FOLCROFT"
      }
    };
  },
  
  computed: {
    formattedDateRange() {
      if (!this.dateRange || this.dateRange.length < 2) return 'Select dates';
      return `${this.dateRange[0]} - ${this.dateRange[1]}`;
    },
    
    hasData() {
      return this.rawSummaryData && this.rawSummaryData.total_sales > 0;
    },
    
    hasActiveFilters() {
      return this.filters.location !== null || 
             this.filters.salesperson !== null || 
             this.filters.paymentMethod !== null ||
             this.filters.saleType !== null;
    },
    
    locationOptions() {
      if (!this.packet.locations && !this.rawLocationSummary) return [];
      if (this.packet.locations && this.packet.locations.length) {
        return this.packet.locations.map(l => ({ value: l.value, text: l.text }));
      }
      if (this.rawLocationSummary) {
        return Object.keys(this.rawLocationSummary).map(id => ({
          value: id,
          text: this.getLocationName(id)
        }));
      }
      return [];
    },
    
    salespersonOptions() {
      if (!this.rawSalespersonSummary) return [];
      return Object.keys(this.rawSalespersonSummary).map(name => ({ value: name, text: name }));
    },
    
    paymentMethodOptions() {
      if (!this.transactions || !this.transactions.length) return [];
      const methods = new Set();
      this.transactions.forEach(t => {
        if (t.payment_methods && t.payment_methods.length) {
          t.payment_methods.forEach(m => {
            let cleanMethod = m;
            if (m.includes('FINANCE - ') || m.toUpperCase().includes('FINANCE')) {
              cleanMethod = m.replace(/FINANCE\s*-\s*/i, '').trim();
              if (this.financeMethods.some(f => cleanMethod.toUpperCase().includes(f))) {
                methods.add('FINANCE');
                return;
              }
            }
            
            if (this.financeMethods.some(f => m.toUpperCase().includes(f))) {
              methods.add('FINANCE');
            } else if (this.taxExemptMethods.some(t => m.toUpperCase().includes(t))) {
              methods.add('TAX EXEMPT');
            } else {
              methods.add(cleanMethod);
            }
          });
        }
      });
      return Array.from(methods).map(m => ({ value: m, text: m }));
    },
    
    saleTypeOptions() {
      return [
        { value: null, text: 'All Types' },
        { value: 'regular', text: 'Regular Sales' },
        { value: 'layaway', text: 'Layaway' },
        { value: 'refund', text: 'Refunds' }
      ];
    },
    
    activeFilterCount() {
      return Object.values(this.filters).filter(v => v !== null).length;
    },
    
    activePeriodLabel() {
      return this.activePeriod ? this.periodMap[this.activePeriod] || this.activePeriod : '';
    },
    
    activePeriodValue() {
      return this.activePeriod ? this.kpiData[this.activePeriod] || 0 : 0;
    },
    
    activePeriodCount() {
      return this.activePeriod ? this.kpiData[`${this.activePeriod}_count`] || 0 : 0;
    },
    
    periodTabs() {
      const d = this.kpiData;
      
      return [
        { key: 'last_week', label: 'Last Week', value: d.last_week || 0, count: d.last_week_count || 0, change: d.last_week_change },
        { key: 'this_week', label: 'This Week', value: d.this_week || 0, count: d.this_week_count || 0, change: d.this_week_change },
        { key: 'last_month', label: 'Last Month', value: d.last_month || 0, count: d.last_month_count || 0, change: d.last_month_change },
        { key: 'this_month', label: 'This Month', value: d.this_month || 0, count: d.this_month_count || 0, change: d.this_month_change },
        { key: 'yesterday', label: 'Yesterday', value: d.yesterday || 0, count: d.yesterday_count || 0, change: d.yesterday_change },
        { key: 'today', label: 'Today', value: d.today || 0, count: d.today_count || 0, change: d.today_change }
      ];
    },
    
    // Filtered summary data
    filteredSummaryData() {
      if (!this.filteredTransactions || !this.filteredTransactions.length) {
        return {
          total_sales: 0,
          total_net_sales: 0,
          total_orders: 0
        };
      }
      
      let totalSales = 0;
      let totalNet = 0;
      
      this.filteredTransactions.forEach(t => {
        const amount = Math.abs(t.total_dollar_amount || 0);
        totalSales += amount;
        totalNet += (t.total_dollar_amount || 0);
      });
      
      return {
        total_sales: totalSales,
        total_net_sales: totalNet,
        total_orders: this.filteredTransactions.length
      };
    },
    
    // Filtered location stats
    filteredLocationStats() {
      if (!this.filteredTransactions || !this.filteredTransactions.length) return [];
      
      const locationMap = {};
      this.filteredTransactions.forEach(t => {
        const id = t.location_id;
        if (!locationMap[id]) {
          locationMap[id] = {
            sales: 0,
            net: 0,
            orders: 0
          };
        }
        locationMap[id].sales += Math.abs(t.total_dollar_amount || 0);
        locationMap[id].net += (t.total_dollar_amount || 0);
        locationMap[id].orders += 1;
      });
      
      const totalNet = Object.values(locationMap).reduce((sum, l) => sum + l.net, 0) || 1;
      
      return Object.keys(locationMap).map(id => ({
        location_id: id,
        location_name: this.getLocationName(id),
        sales: locationMap[id].sales,
        net: locationMap[id].net,
        orders: locationMap[id].orders,
        percentage: ((locationMap[id].net || 0) / totalNet * 100).toFixed(1)
      })).sort((a, b) => b.sales - a.sales);
    },
    
    filteredTotalLocationSales() {
      return this.filteredLocationStats.reduce((sum, l) => sum + l.sales, 0);
    },
    
    filteredTotalLocationNet() {
      return this.filteredLocationStats.reduce((sum, l) => sum + l.net, 0);
    },
    
    filteredTotalLocationOrders() {
      return this.filteredLocationStats.reduce((sum, l) => sum + l.orders, 0);
    },
    
    filteredSalespersonStats() {
      if (!this.filteredTransactions || !this.filteredTransactions.length) return [];
      
      const personMap = {};
      this.filteredTransactions.forEach(t => {
        const name = t.username || 'Unknown';
        if (!personMap[name]) {
          personMap[name] = {
            sales: 0,
            net: 0,
            orders: 0
          };
        }
        personMap[name].sales += Math.abs(t.total_dollar_amount || 0);
        personMap[name].net += (t.total_dollar_amount || 0);
        personMap[name].orders += 1;
      });
      
      const totalNet = Object.values(personMap).reduce((sum, p) => sum + p.net, 0) || 1;
      
      return Object.keys(personMap).map(name => ({
        salesperson: name,
        sales: personMap[name].sales,
        net: personMap[name].net,
        orders: personMap[name].orders,
        avg_order: personMap[name].orders > 0 ? personMap[name].net / personMap[name].orders : 0,
        percentage: ((personMap[name].net || 0) / totalNet * 100).toFixed(1)
      })).sort((a, b) => b.sales - a.sales);
    },
    
    filteredTotalSalespersonSales() {
      return this.filteredSalespersonStats.reduce((sum, p) => sum + p.sales, 0);
    },
    
    filteredTotalSalespersonNet() {
      return this.filteredSalespersonStats.reduce((sum, p) => sum + p.net, 0);
    },
    
    filteredTotalSalespersonOrders() {
      return this.filteredSalespersonStats.reduce((sum, p) => sum + p.orders, 0);
    },
    
    filteredTotalSalespersonAvgOrder() {
      const totalNet = this.filteredTotalSalespersonNet;
      const totalOrders = this.filteredTotalSalespersonOrders;
      return totalOrders > 0 ? totalNet / totalOrders : 0;
    },
    
    paymentMethodStats() {
      if (!this.transactions || !this.transactions.length) return [];
      
      const paymentTotals = {};
      const paymentCounts = {};
      const subTotals = {};
      
      // Use filtered transactions
      const transactionsToUse = this.filteredTransactions.length ? this.filteredTransactions : this.transactions;
      
      transactionsToUse.forEach(transaction => {
        if (!transaction.payment_methods || !transaction.payment_methods.length) return;
        
        transaction.payment_methods.forEach(method => {
          if (!method) return;
          
          const amount = Math.abs(transaction.total_dollar_amount || 0);
          
          let cleanMethod = method;
          let isFinance = false;
          
          // Check if it's a finance method - more robust matching
          const methodUpper = method.toUpperCase();
          if (methodUpper.includes('ACIMA CREDIT') || 
              methodUpper.includes('AMERICAN FIRST') || 
              methodUpper.includes('WELLS FARGO') || 
              methodUpper.includes('SNAP FINANCE') || 
              methodUpper.includes('PROGRESSIVE') || 
              methodUpper.includes('CHECK PERSONAL') ||
              methodUpper.includes('FINANCE')) {
            isFinance = true;
            // Clean up the method name
            cleanMethod = method.replace(/FINANCE\s*-\s*/i, '').trim();
            if (!cleanMethod || cleanMethod === method) {
              // If no clean method found, use the original but remove "FINANCE"
              cleanMethod = method.replace(/FINANCE/i, '').trim();
            }
          }
          
          if (methodUpper.includes('TAX EXEMPT')) {
            const groupKey = 'TAX EXEMPT';
            if (!paymentTotals[groupKey]) {
              paymentTotals[groupKey] = 0;
              paymentCounts[groupKey] = 0;
              subTotals[groupKey] = {};
            }
            paymentTotals[groupKey] += amount;
            paymentCounts[groupKey] += 1;
            if (!subTotals[groupKey][method]) {
              subTotals[groupKey][method] = 0;
            }
            subTotals[groupKey][method] += amount;
          } 
          else if (isFinance) {
            const groupKey = 'FINANCE';
            if (!paymentTotals[groupKey]) {
              paymentTotals[groupKey] = 0;
              paymentCounts[groupKey] = 0;
              subTotals[groupKey] = {};
            }
            paymentTotals[groupKey] += amount;
            paymentCounts[groupKey] += 1;
            if (!subTotals[groupKey][cleanMethod]) {
              subTotals[groupKey][cleanMethod] = 0;
            }
            subTotals[groupKey][cleanMethod] += amount;
          } 
          else {
            if (!paymentTotals[method]) {
              paymentTotals[method] = 0;
              paymentCounts[method] = 0;
            }
            paymentTotals[method] += amount;
            paymentCounts[method] += 1;
          }
        });
      });
      
      const total = Object.values(paymentTotals).reduce((s, v) => s + v, 0) || 1;
      
      const result = Object.keys(paymentTotals).map(method => {
        const subMethods = [];
        if (subTotals[method]) {
          Object.keys(subTotals[method]).forEach(sub => {
            subMethods.push({
              name: sub,
              amount: subTotals[method][sub]
            });
          });
        }
        return {
          method: method,
          amount: paymentTotals[method],
          count: paymentCounts[method] || 1,
          percentage: (paymentTotals[method] / total * 100).toFixed(1),
          subMethods: subMethods.sort((a, b) => b.amount - a.amount)
        };
      }).sort((a, b) => b.amount - a.amount);
      
      return result;
    },
    
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredTransactions.slice(start, start + this.pageSize);
    }
  },
  
  watch: {
    paymentMethodStats: {
      handler() {
        this.$nextTick(() => {
          this.renderPieChart();
        });
      },
      deep: true
    }
  },
  
  mounted() {
    this.loadUserData();
    this.setDefaultDateRange();
    // Fetch ONLY KPI data on load (for the period tabs)
    this.$nextTick(() => {
      this.fetchAllKPIs();
    });
  },
  
  beforeDestroy() {
    if (this.pieChart) this.pieChart.destroy();
  },
  
  methods: {
    formatNumber(num) {
      if (num === undefined || num === null || isNaN(num)) return '0.00';
      return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    
    getDateRange(period) {
      const now = moment();
      let start, end;
      switch(period) {
        case 'today': 
          start = now.format('MM-DD-YYYY'); 
          end = now.format('MM-DD-YYYY'); 
          break;
        case 'yesterday': 
          start = now.clone().subtract(1, 'day').format('MM-DD-YYYY'); 
          end = now.clone().subtract(1, 'day').format('MM-DD-YYYY'); 
          break;
        case 'this_week': 
          start = now.clone().startOf('week').format('MM-DD-YYYY'); 
          end = now.clone().endOf('week').format('MM-DD-YYYY'); 
          break;
        case 'last_week': 
          start = now.clone().subtract(1, 'week').startOf('week').format('MM-DD-YYYY'); 
          end = now.clone().subtract(1, 'week').endOf('week').format('MM-DD-YYYY'); 
          break;
        case 'this_month': 
          start = now.clone().startOf('month').format('MM-DD-YYYY'); 
          end = now.clone().endOf('month').format('MM-DD-YYYY'); 
          break;
        case 'last_month': 
          start = now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY'); 
          end = now.clone().subtract(1, 'month').endOf('month').format('MM-DD-YYYY'); 
          break;
        default: return null;
      }
      return [start, end];
    },
    
    async fetchKPI(period) {
      const dateRange = this.getDateRange(period);
      if (!dateRange) return null;
      
      const data = {
        username: this.userDetails.username,
        dates: dateRange,
        location_id: this.filters.location,
        salesperson: this.filters.salesperson,
        current_user_id: this.userDetails.id,
        userid: this.userDetails.id
      };
      
      try {
        const response = await axios.post('/api/reports/reports_optimized/', data, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.data.result && !response.data.error) {
          const result = response.data.result;
          return {
            sales: result.summary ? result.summary.total_sales : 0,
            count: result.summary ? result.summary.total_orders : 0
          };
        }
        return { sales: 0, count: 0 };
      } catch (error) {
        console.error(`Error fetching ${period}:`, error);
        return { sales: 0, count: 0 };
      }
    },
    
    async fetchAllKPIs() {
      const periods = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month'];
      const results = await Promise.all(periods.map(p => this.fetchKPI(p)));
      
      const map = {
        today: 0, yesterday: 1, this_week: 2, last_week: 3, this_month: 4, last_month: 5
      };
      
      Object.keys(map).forEach((key, i) => {
        if (results[i]) {
          this.kpiData[key] = results[i].sales || 0;
          this.kpiData[`${key}_count`] = results[i].count || 0;
        }
      });
      
      this.kpiData.this_week_change = this.kpiData.last_week > 0 ? 
        ((this.kpiData.this_week - this.kpiData.last_week) / this.kpiData.last_week * 100) : null;
      this.kpiData.this_month_change = this.kpiData.last_month > 0 ? 
        ((this.kpiData.this_month - this.kpiData.last_month) / this.kpiData.last_month * 100) : null;
      this.kpiData.yesterday_change = this.kpiData.yesterday > 0 ? 
        ((this.kpiData.today - this.kpiData.yesterday) / this.kpiData.yesterday * 100) : null;
    },
    
    async fetchMainData() {
      if (!this.dateRange || this.dateRange.length < 2) {
        this.$bvToast.toast('Please select a date range', { title: 'Warning', variant: 'warning' });
        return;
      }
      
      this.isLoading = true;
      this.showDetailed = false;
      
      try {
        await this.fetchSummaryData();
        await this.fetchDetailedData();
        this.applyFilters();
        
        this.$nextTick(() => {
          this.renderPieChart();
        });
      } catch (error) {
        console.error('Error:', error);
        this.$bvToast.toast('Error fetching data', { title: 'Error', variant: 'danger' });
      } finally {
        this.isLoading = false;
      }
    },
    
    async fetchSummaryData() {
      const data = {
        username: this.userDetails.username,
        dates: this.dateRange,
        location_id: this.filters.location,
        salesperson: this.filters.salesperson,
        current_user_id: this.userDetails.id,
        userid: this.userDetails.id
      };
      
      const response = await axios.post('/api/reports/reports_optimized/', data, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.result && !response.data.error) {
        const result = response.data.result;
        this.rawSummaryData = result.summary || {};
        this.rawLocationSummary = result.location_summary || {};
        this.rawSalespersonSummary = result.salesperson_summary || {};
      }
    },
    
    async fetchDetailedData() {
      const data = {
        username: this.userDetails.username,
        dates: this.dateRange,
        location_id: this.filters.location,
        salesperson: this.filters.salesperson,
        current_user_id: this.userDetails.id,
        userid: this.userDetails.id
      };
      
      const response = await axios.post('/api/reports/reports_v3/', data, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.result && !response.data.error) {
        this.transactions = response.data.result.map(item => ({
          ...item,
          payment_methods: item.payment_methods_with_amounts ?
            item.payment_methods_with_amounts.map(p => p.method) :
            item.payment_methods || []
        }));
      } else {
        this.transactions = [];
      }
    },
    
    async fetchAllData() {
      this.isLoading = true;
      try {
        await this.fetchAllKPIs();
        await this.fetchMainData();
      } catch (error) {
        console.error('Error fetching all data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    selectPeriod(key) {
      // If clicking the same period, clear the filter
      if (this.activePeriod === key) {
        this.clearPeriodFilter();
        return;
      }
      
      this.activePeriod = key;
      const dateRange = this.getDateRange(key);
      if (dateRange) {
        this.dateRange = dateRange;
        // Fetch data for the selected period
        this.fetchAllData();
      }
    },
    
    clearPeriodFilter() {
      this.activePeriod = null;
      this.setDefaultDateRange();
      // Clear ALL data including KPI and detailed data, then reload KPI
      this.rawSummaryData = null;
      this.rawLocationSummary = {};
      this.rawSalespersonSummary = {};
      this.transactions = [];
      this.filteredTransactions = [];
      this.kpiData = {
        today: 0, yesterday: 0, this_week: 0, last_week: 0, this_month: 0, last_month: 0,
        today_count: 0, yesterday_count: 0, this_week_count: 0, last_week_count: 0,
        this_month_count: 0, last_month_count: 0, this_week_change: null, this_month_change: null, yesterday_change: null
      };
      // Reset filters
      this.filters = {
        location: null,
        salesperson: null,
        paymentMethod: null,
        saleType: null
      };
      this.showDetailed = false;
      // Refresh KPI data after clearing
      this.fetchAllKPIs();
    },
    
    isPaymentMethodActive(method) {
      if (this.filters.paymentMethod === method) return true;
      if (method === 'FINANCE' && this.financeMethods.includes(this.filters.paymentMethod)) return true;
      if (method === 'TAX EXEMPT' && this.taxExemptMethods.includes(this.filters.paymentMethod)) return true;
      return false;
    },
    
    drillDownPaymentMethod(item) {
      if (item.method === 'FINANCE' || item.method === 'TAX EXEMPT') {
        this.drillDown('paymentMethod', item.method);
      } else {
        this.drillDown('paymentMethod', item.method);
      }
    },
    
    drillDown(type, value) {
      if (type === 'location') {
        this.filters.location = this.filters.location === value ? null : value;
        this.filters.salesperson = null;
        this.filters.paymentMethod = null;
      } else if (type === 'salesperson') {
        this.filters.salesperson = this.filters.salesperson === value ? null : value;
        this.filters.location = null;
        this.filters.paymentMethod = null;
      } else if (type === 'paymentMethod') {
        this.filters.paymentMethod = this.filters.paymentMethod === value ? null : value;
      }
      
      if (this.activePeriod) {
        this.fetchMainData();
      }
      
      if (this.hasActiveFilters) {
        this.showDetailed = true;
      } else {
        this.showDetailed = false;
      }
    },
    
    onFilterChange() {
      if (this.activePeriod) {
        this.fetchMainData();
      }
    },
    
    applyFilters() {
      if (!this.transactions || !this.transactions.length) {
        this.filteredTransactions = [];
        return;
      }
      
      this.filteredTransactions = this.transactions.filter(item => {
        if (this.filters.location && item.location_id != this.filters.location) return false;
        if (this.filters.salesperson && item.username !== this.filters.salesperson) return false;
        if (this.filters.paymentMethod) {
          if (this.filters.paymentMethod === 'FINANCE') {
            if (!item.payment_methods || !item.payment_methods.some(m => {
              const clean = m.replace(/FINANCE\s*-\s*/i, '').trim();
              return this.financeMethods.some(f => clean.toUpperCase().includes(f)) || 
                     m.toUpperCase().includes('FINANCE');
            })) return false;
          } else if (this.filters.paymentMethod === 'TAX EXEMPT') {
            if (!item.payment_methods || !item.payment_methods.some(m => {
              return this.taxExemptMethods.some(t => m.toUpperCase().includes(t));
            })) return false;
          } else {
            if (!item.payment_methods || !item.payment_methods.some(m => {
              const clean = m.replace(/FINANCE\s*-\s*/i, '').trim();
              return clean === this.filters.paymentMethod || 
                     m === this.filters.paymentMethod ||
                     m.toUpperCase().includes(this.filters.paymentMethod);
            })) return false;
          }
        }
        if (this.filters.saleType === 'refund' && !item.is_refund_entry) return false;
        if (this.filters.saleType === 'layaway' && item.sale_type !== 'layaway') return false;
        if (this.filters.saleType === 'regular' && (item.is_refund_entry || item.sale_type === 'layaway')) return false;
        return true;
      });
      
      this.currentPage = 1;
    },
    
    clearAllFilters() {
      this.filters = { location: null, salesperson: null, paymentMethod: null, saleType: null };
      this.showDetailed = false;
      if (this.activePeriod) {
        this.fetchMainData();
      }
    },
    
    onDateRangeChange() {
      if (this.dateRange && this.dateRange.length === 2) {
        // Clear detailed data but keep KPI data
        this.rawSummaryData = null;
        this.rawLocationSummary = {};
        this.rawSalespersonSummary = {};
        this.transactions = [];
        this.filteredTransactions = [];
        // Refresh KPI data for new date range
        this.fetchAllKPIs();
        // If a period is active, refresh data for it
        if (this.activePeriod) {
          this.fetchMainData();
        }
      }
    },
    
    renderPieChart() {
      const canvas = this.$refs.pieChart;
      if (!canvas) {
        console.warn('Pie chart canvas not found');
        return;
      }
      
      if (this.pieChart) {
        this.pieChart.destroy();
        this.pieChart = null;
      }
      
      const stats = this.paymentMethodStats;
      if (!stats || !stats.length) {
        this.pieChart = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: ['No Data'],
            datasets: [{
              data: [1],
              backgroundColor: ['#e2e8f0']
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            cutout: '60%'
          }
        });
        return;
      }
      
      let labels = stats.map(s => s.method);
      let data = stats.map(s => s.amount);
      let backgroundColor = this.colors.slice(0, stats.length);
      
      // If FINANCE is selected as filter, show its sub-methods in the pie chart
      if (this.filters.paymentMethod === 'FINANCE') {
        const financeItem = stats.find(s => s.method === 'FINANCE');
        if (financeItem && financeItem.subMethods && financeItem.subMethods.length > 0) {
          const subLabels = financeItem.subMethods.map(s => s.name);
          const subData = financeItem.subMethods.map(s => s.amount);
          const subColors = this.colors.slice(0, subData.length);
          
          const otherStats = stats.filter(s => s.method !== 'FINANCE');
          labels = [...otherStats.map(s => s.method), ...subLabels];
          data = [...otherStats.map(s => s.amount), ...subData];
          backgroundColor = [
            ...this.colors.slice(0, otherStats.length),
            ...subColors
          ];
        }
      }
      
      this.pieChart = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: backgroundColor,
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                font: { size: 11 },
                padding: 10,
                usePointStyle: true,
                pointStyle: 'circle'
              }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                  return `${ctx.label}: $${ctx.parsed.toLocaleString()} (${percentage}%)`;
                }
              }
            }
          },
          cutout: '60%'
        }
      });
    },
    
    loadUserData() {
      try {
        this.packet.userDetails = this.userDetails || JSON.parse(localStorage.getItem('userDetails') || '{}');
        const locations = localStorage.getItem('locations');
        if (locations) this.packet.locations = JSON.parse(locations);
        const packet = JSON.parse(localStorage.getItem('packet') || '{}');
        if (packet.locations) this.packet.locations = packet.locations;
      } catch (e) {
        console.error('Error loading user data:', e);
      }
    },
    
    setDefaultDateRange() {
      const today = moment();
      this.dateRange = [
        today.clone().startOf('week').format('MM-DD-YYYY'),
        today.clone().endOf('week').format('MM-DD-YYYY')
      ];
    },
    
    getLocationName(id) {
      if (this.locationMap[id]) return this.locationMap[id];
      if (!this.packet.locations) return `Location ${id}`;
      const loc = this.packet.locations.find(l => l.value == id);
      return loc ? loc.text : `Location ${id}`;
    },
    
    formatDate(ts) {
      if (!ts) return '-';
      return moment(ts).format('MM-DD-YYYY HH:mm');
    },
    
    exportCSV() {
      if (!this.filteredTransactions.length) {
        this.$bvToast.toast('No data to export', { title: 'Info', variant: 'info' });
        return;
      }
      
      const headers = ['Date', 'Invoice', 'Location', 'Salesperson', 'Payment', 'Amount', 'Type'];
      const rows = this.filteredTransactions.map(item => [
        item.ts,
        item.invoice_id,
        this.getLocationName(item.location_id),
        item.username,
        (item.payment_methods || []).join('; '),
        (item.total_dollar_amount || 0).toFixed(2),
        item.is_refund_entry ? 'Refund' : 'Sale'
      ]);
      
      const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_report_${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      this.$bvToast.toast('Export successful!', { title: 'Success', variant: 'success' });
    }
  }
};
</script>

<style scoped>
.glass2 {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  padding: 20px 30px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.text-muted-foreground {
  color: #94a3b8;
  font-size: 12px;
}

.date-badge {
  background: rgba(255, 255, 255, 0.2);
  color: #1e293b;
  border: 1px solid rgba(226, 232, 240, 0.5);
  padding: 6px 12px;
  border-radius: 20px;
}

/* Filters */
.filters-bar { margin-bottom: 16px; }
.filters-card { 
  border: none; 
  border-radius: 12px; 
  box-shadow: 0 1px 3px rgba(0,0,0,0.06); 
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
}
.filter-label { font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 2px; display: block; text-transform: uppercase; letter-spacing: 0.3px; }
.filter-select { border-radius: 6px; font-size: 12px; background: rgba(248, 250, 252, 0.8); border-color: #e2e8f0; }
.custom-datepicker {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.8);
}
.analyze-btn { border-radius: 6px; padding: 4px 12px; }
.clear-filters { font-size: 12px; color: #6366f1; }
.clear-filters:hover { color: #4f46e5; text-decoration: none; }

.period-tabs-wrapper {
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.period-tabs {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
}

.period-tab {
  padding: 12px 8px;
  text-align: center;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  border: 2px solid transparent;
}

.period-tab:hover {
  background: rgba(241, 245, 249, 0.8);
  transform: translateY(-2px);
}

.period-tab.active {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
  transform: translateY(-2px);
}

.period-tab.active .tab-label,
.period-tab.active .tab-value,
.period-tab.active .tab-change,
.period-tab.active .tab-sub {
  color: white;
}

.period-tab.active .tab-change.positive { color: #a7f3d0; }
.period-tab.active .tab-change.negative { color: #fca5a5; }

.tab-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 600;
}

.tab-value {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 2px 0;
}

.tab-change {
  font-size: 10px;
  font-weight: 600;
}

.tab-sub {
  font-size: 9px;
  color: #94a3b8;
  margin-top: 2px;
}

.tab-change.positive { color: #10b981; }
.tab-change.negative { color: #ef4444; }

.active-period-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  border-radius: 10px;
  margin-bottom: 16px;
  border: 2px solid #6366f1;
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.banner-label {
  font-size: 14px;
  font-weight: 600;
  color: #4338ca;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.banner-value {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

.banner-sub {
  font-size: 12px;
  color: #64748b;
}

.banner-clear {
  border-radius: 50%;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.8);
  border: 1px solid #e2e8f0;
}

.content-wrapper {
  transition: all 0.3s ease;
}

/* Summary Stats - Glass Cards */
.summary-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  margin-bottom: 20px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(5px);
  border-radius: 12px;
  padding: 15px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.5);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 15px;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  font-size: 1.8rem;
  min-width: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-content {
  flex: 1;
  text-align: right;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 0.7rem;
  color: #718096;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.pie-chart-container {
  height: 250px;
  width: 100%;
  max-width: 350px;
  margin: 0 auto;
}

.table-card { border: none; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); overflow: hidden; background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(5px); }
.table-header { background: rgba(248, 250, 252, 0.9); padding: 8px 12px; font-weight: 600; font-size: 14px; border-bottom: 1px solid #e2e8f0; }
.table-wrap { overflow-x: auto; max-height: 300px; overflow-y: auto; }

.performance-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}

.performance-table th,
.performance-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f1f5f9;
}

.performance-table th {
  background: rgba(250, 251, 252, 0.9);
  font-weight: 600;
  color: #475569;
  position: sticky;
  top: 0;
  z-index: 1;
}

.performance-table tbody tr {
  cursor: pointer;
  transition: background 0.15s;
}

.performance-table tbody tr:hover {
  background: rgba(241, 245, 249, 0.8);
}

.performance-table tbody tr.drill-active {
  background: #dbeafe !important;
  border-left: 3px solid #6366f1;
}

.performance-table tfoot tr {
  background: rgba(248, 250, 252, 0.9);
  font-weight: 600;
}

.payment-methods { max-height: 320px; overflow-y: auto; }
.payment-item {
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s;
}

.payment-item:hover { background: rgba(241, 245, 249, 0.8); }
.payment-item.drill-active { background: #dbeafe; }

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.payment-name { font-weight: 600; font-size: 13px; display: flex; align-items: center; gap: 8px; }
.payment-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.payment-amount { font-weight: 600; font-size: 13px; }

.payment-bar {
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  margin: 4px 0;
  overflow: hidden;
}

.payment-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s;
}

.sub-methods {
  padding: 4px 8px 4px 28px;
  border-left: 2px solid #e2e8f0;
  margin-left: 16px;
}

.sub-method-item {
  display: flex;
  justify-content: space-between;
  padding: 2px 4px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.sub-method-item:hover {
  background: rgba(241, 245, 249, 0.8);
}

.sub-method-item.drill-active {
  background: #dbeafe;
}

.sub-method-name { color: #475569; }
.sub-method-amount { font-weight: 600; color: #1e293b; }

.detailed-section { margin-top: 16px; }

/* Empty State */
.empty-dashboard {
  text-align: center;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  border-radius: 16px;
}

.empty-icon {
  font-size: 4rem;
  color: #cbd5e0;
  margin-bottom: 20px;
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #dc3545;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 1024px) {
  .period-tabs { grid-template-columns: repeat(3, 1fr); }
  .summary-stats { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 768px) {
  .glass2 { padding: 15px; }
  .period-tabs { grid-template-columns: repeat(2, 1fr); }
  .period-tab { padding: 8px 4px; }
  .tab-value { font-size: 15px; }
  .banner-content { flex-wrap: wrap; gap: 8px; }
  .banner-value { font-size: 20px; }
  .summary-stats { grid-template-columns: 1fr 1fr; }
  .stat-value { font-size: 1rem; }
  .pie-chart-container { max-width: 250px; }
}

@media (max-width: 576px) {
  .period-tabs { grid-template-columns: repeat(2, 1fr); }
  .period-tab { padding: 6px 4px; }
  .tab-value { font-size: 13px; }
  .tab-label { font-size: 8px; }
  .summary-stats { grid-template-columns: 1fr; }
  .pie-chart-container { max-width: 200px; height: 200px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Homepagecomponents/SalesDashboard2.vue